import { getArrayRemove, getArrayUnion, getFirestore } from '../../system/firebase';

import { ACCOUNT_STATUS_PENDING_MEMBER_SETUP, SNACK_BAR_MESSAGE_OPERATION_FAILED, SNACK_BAR_MESSAGE_SAVE } from '../constants';
import { convertFirebaseTimestampsToDate } from '../dateHelper';
import { getFullNameByUser } from '../textHelper';
import { mapDefinedFields } from '../dtoHelper';

import _get from 'lodash/get';
import _set from 'lodash/set';
import _find from 'lodash/find';
import _map from 'lodash/map';
import _forEach from 'lodash/forEach';

export const loadOpenInvitations = async ({ email, setData }) => {
    const db = getFirestore();

    //-------------------------------------------------------------------------------
    // fetch invitation list
    //-------------------------------------------------------------------------------
    const invitationsQuery = db
        .collection('invitations')
        .where('email', '==', email)
        .where('acceptDt', '==', null)
        .where('declineDt', '==', null)
        .orderBy('inviteDt', 'asc')
        .limit(50);
    const querySnapshot = await invitationsQuery.get();
    const invitations = [];

    querySnapshot.forEach(async doc => {
        let data = doc.data();

        // add data to list
        data = convertFirebaseTimestampsToDate(data, ['inviteDt', 'acceptDt']);
        invitations.push({ ...data, code: doc.id });
    });

    //-------------------------------------------------------------------------------
    // For each invitation, fetch the inviter employer data
    //-------------------------------------------------------------------------------
    await Promise.all(
        _map(invitations, async (invitation, idx) => {
            let inviterData = {};

            // obtain data for employer query
            const employerId = _get(invitation, 'employerId');
            const inviterUserId = _get(invitation, 'inviterUserId');

            // fetch employer data
            const employerRef = db.doc(`employers/${employerId}`);
            const employerDoc = await employerRef.get();
            if (employerDoc.exists) {
                const empData = employerDoc.data();
                const empMembers = _get(empData, 'members', []);
                const inviterMember = _find(empMembers, member => (member.userId = inviterUserId)) || _get(empData, 'members[0]'); // use first member if inviter not found
                const inviterName = getFullNameByUser(inviterMember);
                inviterData = {
                    companyName: _get(empData, 'companyName'),
                    companyProfilePicUrl: _get(empData, 'profilePicUrl'),
                    inviterName: inviterName,
                    inviterProfilePicUrl: _get(inviterMember, 'profilePicUrl'),
                };

                // merge the employer data with the invitation data
                _set(invitations, `[${idx}]`, { ...invitation, ...inviterData });
            }
        }),
    );

    if (setData) setData(invitations);

    return invitations;
};

export const saveEmployerAccountLinkage = async ({ inviteeUserId, inviteeUserData, invitationList = [], setSnackbarMessage, setSnackbarErrorMessage }) => {
    const db = getFirestore();
    const now = new Date();

    //-------------------------------------------------------------------------------------------
    // Obtain the invitation code and auth user info
    //-------------------------------------------------------------------------------------------
    try {
        _forEach(invitationList, async invitationData => {
            await db.runTransaction(async transaction => {
                const isAccept = _get(invitationData, 'isAccept');

                const inviteCode = _get(invitationData, 'code');
                const inviterEmployerId = _get(invitationData, 'employerId');
                const inviteeEmail = _get(invitationData, 'email');

                //-------------------------------------------------------------------------------------------
                // prep for employers
                //-------------------------------------------------------------------------------------------
                const employerRef = db.doc(`employers/${inviterEmployerId}`);

                //-------------------------------------------------------------------------------------------
                // prep for users
                //-------------------------------------------------------------------------------------------
                const inviteeUserRef = db.doc(`users/${inviteeUserId}`);

                //-------------------------------------------------------------------------------------------
                // prep for invitations
                //-------------------------------------------------------------------------------------------
                const invitationRef = db.doc(`invitations/${inviteCode}`);

                if (isAccept) {
                    //-------------------------------------------------------------------------------------------
                    // read employers
                    //-------------------------------------------------------------------------------------------
                    const employerDoc = await transaction.get(employerRef);
                    const inviterEmployerData = employerDoc.data();
                    const employerDto = { companyName: _get(inviterEmployerData, 'companyName'), logoUrl: _get(inviterEmployerData, 'profilePicUrl') };
                    // const inviterEmployerMembers = _get(inviterEmployerData, 'members');

                    const employerMemberDto = {
                        email: inviteeEmail,
                        firstName: _get(inviteeUserData, 'employers[0].firstName', null),
                        middleName: _get(inviteeUserData, 'employers[0].middleName', null),
                        lastName: _get(inviteeUserData, 'employers[0].lastName', null),
                        profilePicUrl: _get(inviteeUserData, 'employers[0].profilePicUrl', null),
                        isAdmin: _get(invitationData, 'isAdmin', false),
                        userId: inviteeUserId,
                    };

                    //-------------------------------------------------------------------------------------------
                    // set up users
                    //-------------------------------------------------------------------------------------------
                    const userEmployerDto = {
                        employerId: inviterEmployerId,
                        firstName: _get(inviteeUserData, 'employers[0].firstName', null),
                        middleName: _get(inviteeUserData, 'employers[0].middleName', null),
                        lastName: _get(inviteeUserData, 'employers[0].lastName', null),
                        profilePicUrl: _get(inviteeUserData, 'employers[0].profilePicUrl', null),
                        status: ACCOUNT_STATUS_PENDING_MEMBER_SETUP,
                        ...employerDto,
                    };

                    //-------------------------------------------------------------------------------------------
                    // write employer
                    //-------------------------------------------------------------------------------------------
                    transaction.update(employerRef, {
                        invited: getArrayRemove(inviteeEmail),
                        members: getArrayUnion(mapDefinedFields(employerMemberDto)),
                    });

                    //-------------------------------------------------------------------------------------------
                    // write users
                    //-------------------------------------------------------------------------------------------
                    transaction.update(inviteeUserRef, {
                        employers: getArrayUnion(mapDefinedFields(userEmployerDto)),
                        isInviteMode: false, // this might be set to false multiple times but in a batch write so it's ok
                    });

                    //-------------------------------------------------------------------------------------------
                    // write invitations
                    //-------------------------------------------------------------------------------------------
                    transaction.update(invitationRef, {
                        acceptDt: now,
                        userId: inviteeUserId,
                    });
                } else {
                    //-------------------------------------------------------------------------------------------
                    // write employer
                    //-------------------------------------------------------------------------------------------
                    transaction.update(employerRef, {
                        inviteDeclined: getArrayUnion(inviteeEmail),
                    });

                    //-------------------------------------------------------------------------------------------
                    // write invitations (declined)
                    //-------------------------------------------------------------------------------------------
                    transaction.update(invitationRef, {
                        declineDt: now,
                        userId: inviteeUserId,
                    });
                }
            });
        });

        setSnackbarMessage(SNACK_BAR_MESSAGE_SAVE);
    } catch (error) {
        console.log('error', error);
        setSnackbarErrorMessage(SNACK_BAR_MESSAGE_OPERATION_FAILED);
    }
};
